import { useState } from "react";
import type { DeepKeys, DeepValue, Updater } from "@tanstack/react-form";
import { Eye, EyeOff } from "lucide-react";
import type { z } from "zod";

import type { FieldInputProps } from "@/components/form/field-input";
import InsideLabelField from "@/components/form/inside-label-field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getOnChangeFromZod } from "@/utils/form";

const FieldInput = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
>({
  Field,
  name,
  schema,
  label,
  onChange,
  validators,
  labelProps,
  gridRow,
  ...props
}: FieldInputProps<TFormData, TName, TSchema, TData>) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Field
      name={name}
      validators={{
        onChange: schema ? getOnChangeFromZod(schema, name) : undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(validators as any), // unsolved typing issue
      }}
      children={(field) => {
        return (
          <InsideLabelField
            errors={field.state.meta.errors}
            gridRow={gridRow}
            {...labelProps}
          >
            <div className="relative w-full">
              <Input
                insideLabel={
                  <label
                    htmlFor={name}
                    className="absolute text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] start-3.5 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto"
                  >
                    {label}
                  </label>
                }
                id={name}
                type={showPassword ? "text" : "password"}
                className="px-3.5 pb-1.5 pt-5 h-[55px] peer"
                placeholder=""
                autoComplete="current-password"
                value={(field.state.value as string | null)?.toString() ?? ""}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  if (onChange) onChange(e);
                  (
                    field.handleChange as (
                      updater: Updater<string | number | null>
                    ) => void
                  )(e.target.value);
                }}
                errors={!!field.state.meta.errors.length}
                {...props}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-2 top-2"
              >
                {showPassword ? (
                  <Eye width={16} height={16} />
                ) : (
                  <EyeOff width={16} height={16} />
                )}
              </Button>
            </div>
          </InsideLabelField>
        );
      }}
    />
  );
};

export default FieldInput;
